import { Component, computed, inject, LOCALE_ID, OnInit, signal } from '@angular/core';
import { PharmacyVTO } from '../../../../services/pharmacy/pharmacy.model';
import { CommonModule } from '@angular/common';
import { ViewPharmacyDetails } from '../../../user-dashboard-component/pharmacies/view-pharmacy-details/view-pharmacy-details';
import { ViewPharmacyHistory } from '../../../user-dashboard-component/pharmacies/view-pharmacy-history/view-pharmacy-history';
import { ViewRequestDetails } from '../../../user-dashboard-component/pharmacies/view-request-details/view-request-details';
import { AssignEmployeeDialog } from '../assign-employee-dialog/assign-employee-dialog';
import { PharmacyReasonDialog } from '../dialogs/pharmacy-reason-dialog/pharmacy-reason-dialog';
import { ApprovePharmacyDialog } from '../dialogs/approve-pharmacy-dialog/approve-pharmacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { PharmacyService } from '../../../../services/pharmacy/pharmacy.service';
import { RequestService } from '../../../../services/pharmacy/request.service';
import { RequestDetailsVTO } from '../../../../services/pharmacy/request.model';
import { StatusBadge } from '../../../shared/status-badge/status-badge';
import { finalize } from 'rxjs/operators';
import { LookupVTO } from '../../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';
import { Loading } from '../../../../utils/loading/loading';
import { HasScopeDirective } from '../../../../services/scope/has-scope.directive';
import { SCOPES } from '../../../../services/scope/scope-constants';
import { PharmacyRequestStatus } from '../../../../pages/admin-dashboard/pharmacies/pharmacies.model';
import { CancelPharmacyDialog } from '../../../user-dashboard-component/pharmacies/cancel-pharmacy-dialog/cancel-pharmacy-dialog';
import { ErrorData } from '../../../../utils/error-data/error-data';
import { EmptyData } from '../../../../utils/empty-data/empty-data';

@Component({
  selector: 'app-view-pharmacy-component',
  imports: [
    CommonModule,
    ViewPharmacyDetails,
    ViewPharmacyHistory,
    ViewRequestDetails,
    AssignEmployeeDialog,
    PharmacyReasonDialog,
    ApprovePharmacyDialog,
    StatusBadge,
    Loading,
    SuccessfulNotification,
    HasScopeDirective,
    CancelPharmacyDialog,
    ErrorData,
    EmptyData,
  ],
  templateUrl: './view-pharmacy-component.html',
  styleUrl: './view-pharmacy-component.scss',
})
export class ViewPharmacyComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private pharmacyService = inject(PharmacyService);
  private requestService = inject(RequestService);
  private localeId = inject(LOCALE_ID);
  private router = inject(Router);

  pharmacyRequestId = signal<string>('');
  readonly SCOPES = SCOPES;

  activeTab = signal<'pharmacy-details' | 'request-details' | 'history-log'>('pharmacy-details');
  pharmacyData = signal<PharmacyVTO | null>(null);
  requestData = signal<RequestDetailsVTO | null>(null);
  currentActions = signal<LookupVTO[]>([]);

  pageLoading = signal<boolean>(true);
  pageError = signal<string | null>(null);
  errorType = signal<'forbidden' | 'not-found' | 'unauthorized' | 'server-error' | null>(null);

  requestDataLoading = signal<boolean>(false);
  requestDataError = signal<string>('');

  successRequestModification = signal<boolean>(false);

  assigningEmployeePharmacyId = signal<number | null>(null);
  rejectingPharmacyId = signal<number | null>(null);
  modifyingPharmacyId = signal<number | null>(null);
  approvingPharmacyId = signal<number | null>(null);
  cancelingPharmacyId = signal<number | null>(null);
  editingPharmacyId = signal<number | null>(null);

  loadingCancel = signal<boolean>(false);
  errorCancel = signal<string>('');
  cancelingPharmacyName = signal<string | null>(null);
  modalType = signal<'assign' | 're-assign'>('assign');

  VIEW_PHARMACY_DETAILS: LookupVTO = {
    id: 6,
    titleEn: 'View Pharmacy Details',
    titleAr: 'عرض تفاصيل الصيدلية',
  };

  isArabic = computed(() => this.localeId.startsWith('ar'));
  isEnglish = computed(() => this.localeId.startsWith('en'));

  getActions(statusId: number) {
    switch (statusId) {
      case PharmacyRequestStatus.Pending:
        this.currentActions.set([
          { id: 1, titleEn: 'Assign Employee', titleAr: 'تعيين موظف' },
          { id: 9, titleEn: 'Cancel Request', titleAr: 'إلغاء الطلب' },
        ]);
        break;
      case PharmacyRequestStatus.InProgress:
        this.currentActions.set([
          { id: 2, titleEn: 'Accept Pharmacy', titleAr: 'قبول الصيدلية' },
          { id: 3, titleEn: 'Reject Pharmacy', titleAr: 'رفض الصيدلية' },
          { id: 4, titleEn: 'Request Modification', titleAr: 'طلب تعديل' },
          { id: 5, titleEn: 'Reassign Employee', titleAr: 'إعادة تعيين موظف' },
        ]);
        break;
      case PharmacyRequestStatus.Feedback:
        this.currentActions.set([
          { id: 10, titleEn: 'Edit Request', titleAr: 'تعديل الطلب' },
          { id: 11, titleEn: 'Cancel Request', titleAr: 'إلغاء الطلب' },
        ]);
        break;
      default:
        this.currentActions.set([]);
    }
  }

  getTranslatedTitle(obj: any): string {
    if (!obj) return '';
    return this.localeId.startsWith('ar') ? obj.titleAr : obj.titleEn;
  }

  isAcceptAction(action: LookupVTO) {
    return action.titleEn?.toLowerCase() === 'accept pharmacy';
  }
  isAssignAction(action: LookupVTO) {
    return action.titleEn?.toLowerCase() === 'assign employee';
  }
  isReassignAction(action: LookupVTO) {
    return action.titleEn?.toLowerCase() === 'reassign employee';
  }
  isCancelAction(action: LookupVTO) {
    return action.titleEn?.toLowerCase() === 'cancel request';
  }
  isEditAction(action: LookupVTO) {
    return action.titleEn?.toLowerCase() === 'edit request';
  }

  isPrimaryAction(action: LookupVTO): boolean {
    const acceptExists = this.currentActions().some((a) => this.isAcceptAction(a));
    if (acceptExists) return this.isAcceptAction(action);
    const editExists = this.currentActions().some((a) => this.isEditAction(a));
    if (editExists) return this.isEditAction(action);
    return false;
  }

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) this.loadPharmacyData(parseInt(id));
    });
  }

  loadPharmacyData(id: number) {
    this.pageLoading.set(true);
    this.pageError.set(null);
    this.errorType.set(null);

    this.pharmacyService
      .getPharmacyById(id)
      .pipe(finalize(() => this.pageLoading.set(false)))
      .subscribe({
        next: (data) => {
          this.pharmacyData.set(data);
          this.loadRequestData(id);
        },
        error: (err) => {
          this.pharmacyData.set(null);
          this.handleError(err);
        },
      });
  }

  loadRequestData(pharmacyId: number) {
    this.requestDataLoading.set(true);
    this.requestDataError.set('');

    this.requestService
      .getPharmacyRequest(pharmacyId)
      .pipe(finalize(() => this.requestDataLoading.set(false)))
      .subscribe({
        next: (data) => {
          this.requestData.set(data);
          if (data?.currentStatus?.id) {
            this.getActions(data.currentStatus.id);
          }
        },
        error: (err) => {
          this.requestDataError.set(
            this.isArabic() ? err?.messageAr || 'حدث خطأ' : err?.messageEn || 'Something went wrong',
          );
          this.handleError(err);
        },
      });
  }

  handleError(err: any) {
    const code = err?.error?.code || err?.code;
    if (code === 'PHARMACY-0003') {
      this.errorType.set('forbidden');
      this.pageError.set(
        this.isArabic() ? 'ليس لديك صلاحية لعرض هذا الطلب' : 'You do not have permission to view this request.',
      );
      return;
    }
    if (code === 'PHARMACY-0001') {
      this.errorType.set('not-found');
      this.pageError.set(this.isArabic() ? 'الصيدلية غير موجودة' : 'Pharmacy not found.');
      return;
    }
  }

  setActiveTab(tab: 'pharmacy-details' | 'request-details' | 'history-log') {
    this.activeTab.set(tab);
  }

  handleAction(action: LookupVTO, pharmacy: PharmacyVTO) {
    const key = action.titleEn?.toLowerCase();
    if (key === 'reassign employee' || key === 'assign employee') {
      this.modalType.set(key === 'reassign employee' ? 're-assign' : 'assign');
      this.assigningEmployeePharmacyId.set(pharmacy.id);
    } else if (key === 'reject pharmacy') {
      this.rejectingPharmacyId.set(pharmacy.id);
    } else if (key === 'request modification') {
      this.modifyingPharmacyId.set(pharmacy.id);
    } else if (key === 'accept pharmacy') {
      this.approvingPharmacyId.set(pharmacy.id);
    } else if (key === 'cancel request') {
      this.cancelingPharmacyId.set(pharmacy.id);
      this.cancelingPharmacyName.set(pharmacy.nickName);
      this.pharmacyRequestId.set(pharmacy.request?.requestId?.toString() ?? '');
    } else if (key === 'edit request') {
      this.router.navigate(['/user/pharmacies/edit', pharmacy.id]);
    }
  }

  closeAssignEmployeeDialog() {
    this.assigningEmployeePharmacyId.set(null);
  }
  onEmployeeAssigned(event: { action: LookupVTO }) {
    this.closeAssignEmployeeDialog();
    this.refreshData();
  }

  closeRejectDialog() {
    this.rejectingPharmacyId.set(null);
  }
  closeEditDialog() {
    this.editingPharmacyId.set(null);
  }

  onReject(item: { action: LookupVTO }) {
    this.requestData.update((p) => (p ? { ...p, currentStatus: item.action } : p));
  }

  closeModifyDialog() {
    this.modifyingPharmacyId.set(null);
  }

  onModify(item: { action: LookupVTO }) {
    this.successRequestModification.set(true);
    this.requestData.update((p) => (p ? { ...p, currentStatus: item.action } : p));
  }

  closeApproveDialog() {
    this.approvingPharmacyId.set(null);
  }
  onApproveSuccess() {
    this.closeApproveDialog();
    this.refreshData();
  }

  closeCancelDialog() {
    this.cancelingPharmacyId.set(null);
    this.cancelingPharmacyName.set(null);
    this.pharmacyRequestId.set('');
    this.errorCancel.set('');
  }

  onRequestCancelled() {
    const id = this.cancelingPharmacyId();
    if (!id) return;
    this.loadingCancel.set(true);
    this.pharmacyService
      .cancelPharmacy(id)
      .pipe(finalize(() => this.loadingCancel.set(false)))
      .subscribe({
        next: () => {
          this.closeCancelDialog();
          this.refreshData();
        },
        error: (err) => {
          this.errorCancel.set(this.isArabic() ? err.messageAr || 'حدث خطأ!' : err.messageEn || 'Some error occurred!');
        },
      });
  }

  refreshData() {
    const id = this.pharmacyData()?.id;
    if (id) this.loadPharmacyData(id);
  }
}
